import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// Importaciones relativas alineadas a la estructura de tu proyecto
import { db } from '../../../../../firebase/config';
import { useAuth } from '../../../../hooks/useAuth'; 

import { useTableState } from '../../hooks/useTableState';
import { useDataHydration } from '../../hooks/useDataHydration';

// --- HELPERS INTERNOS DE PROCESAMIENTO ---
const getMs = (val) => {
    if (!val) return 0;
    if (val && typeof val === 'object' && 'seconds' in val) {
        return val.seconds * 1000;
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? 0 : d.getTime();
};

const getISODate = (val) => {
    if (!val) return '';
    const date = (val && val.seconds) ? new Date(val.seconds * 1000) : new Date(val);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
};

const getImportePriority = (monto) => {
    // Al ser ART en pesos (ARS), aplicamos la escala de prioridades por importe
    const valor = Number(monto || 0);
    if (valor > 300000) {
        return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-400', val: 1 };
    }
    if (valor >= 100000) {
        return { label: 'P2', class: 'bg-orange-200 text-orange-900 border-orange-400', val: 2 };
    }
    return { label: 'P3', class: 'bg-yellow-100 text-yellow-900 border-yellow-300', val: 3 };
};

/**
 * useCobranzaContratosTable - Hook de negocio unificado para el Tablero de Cobranzas de ART.
 */
export const useCobranzaContratosTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [contratos, setContratos] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [showDeepDebtModal, setShowDeepDebtModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false);
    const [activeContrato, setActiveContrato] = useState(null);

    // REFS DE CONCURRENCIA (Para lockeo de filas)
    const activeItemPathRef = useRef(null);

    // Reutilizamos useDataHydration pasándole los contratos para obtener los clientes del cache relacional
    const { clientsCache } = useDataHydration(contratos);

    useEffect(() => {
        // Escuchamos todos los contratos vivos distribuidos bajo clientes/{clienteId}/contratos/{contratoId}
        const q = query(collectionGroup(db, 'contratos'));
        
        const unsubscribe = onSnapshot(q, (snap) => {
            const raw = snap.docs.map(d => {
                const pathParts = d.ref.path.split('/');
                return { 
                    id: d.id, 
                    path: d.ref.path, 
                    // Ruta: clientes/{clienteId}/contratos/{contratoId}
                    clienteId: pathParts[1],
                    contratoId: pathParts[3],
                    ...d.data() 
                };
            });

            // Filtro de Deuda Activa: Solo contratos que tengan artDeudaGlobal > 0
            const activeDebtContracts = raw.filter(c => {
                const deudaglobal = Number(c.artDeudaGlobal || 0);
                return deudaglobal > 0;
            });

            setContratos(activeDebtContracts);
            setLoading(false);
        }, (error) => {
            console.error("Error cargando contratos de ART:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const itemsTable = useMemo(() => {
        return contratos.map(c => {
            const client = clientsCache[c.clienteId] || {};
            
            // Waterfall fallback para Nombre y DNI/CUIT del cliente
            let displayDocNum = c.clienteCuit || c.clienteDni || '-';
            let displayNameStr = `${c.clienteNombre || ''} ${c.clienteApellido || ''}`.trim();

            if (!displayNameStr && client) {
                displayNameStr = client.persona === 'JURIDICA' ? (client.nombre || 'S/D') : `${client.nombre || ''} ${client.apellido || ''}`.trim();
                displayDocNum = client.cuit || client.dni || '-';
            }
            if (!displayNameStr) displayNameStr = 'S/D';

            const priorityInfo = getImportePriority(c.artDeudaGlobal);

            return {
                ...c,
                displayNombre: displayNameStr.toUpperCase(),
                displayDoc: displayDocNum,
                nContrato: c.nContrato || 'S/N',
                compania: c.compania || 'S/C',
                prioridad: priorityInfo.val, // Seteado para filtros directos de prioridad
                
                // Mapeos requeridos de campos específicos de ART
                artEstadoContrato: c.artEstadoContrato || 'ACTIVO',
                artEstadoGestion: c.artEstadoGestion || 'PENDIENTE',
                artEstadoDeuda: c.artEstadoDeuda || 'S/D',
                artUltimoMovimientoResponsable: c.artUltimoMovimientoResponsable || '-',
                canal: c.canal || '-',
                canalNombre: c.canalNombre || c.subcanal || '-',

                // Fechas mapeadas a ISO string de forma síncrona para filtros
                fechaCarga: getISODate(c.artUltimaActualizacion || c.fechaCarga),
                fechaReclamo: getISODate(c.artUltimoReclamoFecha),
                
                // Importe numérico puro para el motor de ordenamiento
                importeDeuda: Number(c.artDeudaGlobal || 0)
            };
        });
    }, [contratos, clientsCache]);

    const needsHydration = useMemo(() => {
        if (contratos.length === 0) return false;
        return contratos.some(c => !clientsCache[c.clienteId]);
    }, [contratos, clientsCache]);

    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, 
            nContrato: true, 
            compania: true, 
            displayNombre: true, 
            displayDoc: true, 
            importeDeuda: true, 
            artEstadoContrato: true, 
            artEstadoGestion: true, 
            artEstadoDeuda: true, 
            fechaReclamo: true, 
            artUltimoMovimientoResponsable: true, 
            canal: true
        },
        recordsPerPage: 100
    });

    const unlockContrato = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { 
            console.error("Error al desbloquear contrato de ART:", e); 
        }
    }, []);

    const handleAction = useCallback(async (item, type) => {
        setActiveContrato(item);

        if (type === 'manage_art_debt') {
            activeItemPathRef.current = item.path;
            setShowDeepDebtModal(true);
            
            // Concurrencia activa: bloqueamos el contrato indicando quién lo está gestionando
            await updateDoc(doc(db, item.path), { 
                bloqueo: { 
                    userId: user?.uid, 
                    userName: profile?.userName || 'Usuario', 
                    timestamp: serverTimestamp() 
                } 
            }).catch(() => { 
                activeItemPathRef.current = null; 
            });
        } else if (type === 'view_contract_detail') {
            setShowContractModal(true);
        } else if (type === 'view_client_ficha') {
            setShowFichaModal(true);
        }
    }, [user, profile]);

    return {
        loading,
        needsHydration,
        clientsCache,
        activeContrato,
        showDeepDebtModal,
        showContractModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDeepDebtModal,
        setShowContractModal,
        setShowFichaModal,
        setActiveContrato,
        handleAction,
        unlockContrato
    };
};